import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import Database from '../types/Database'
import { useTestDataStore } from '../stores/testDataStore'

export interface TickBreakdown {
  showedUp: number
  divedResponsibly: number
  knewGearSetup: number
  goodBuddy: number
  wouldDiveAgain: number
}

export interface PublicProfileData {
  displayName: string
  handle: string | null
  suburb: string | null
  ratingAggregates: Database.RatingAggregates
  tickBreakdown: TickBreakdown
  totalRatings: number
  accreditationType: string | null
  accreditationLevel: string | null
  accreditationVerified: boolean
}

export async function getPublicProfile(
  userId: string
): Promise<PublicProfileData | null> {
  const db = getFirestore()

  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) return null

  const user = userSnap.data() as Omit<Database.User, 'id'>

  const ratingAggregates: Database.RatingAggregates = user.ratingAggregates ?? {
    totalRatingsReceived: 0,
    averageTickScore: 0,
    perfectGuidedDiveRatings: 0
  }

  // Fetch all ratings where this user is the ratee, respecting testData toggle
  const showTestData = useTestDataStore.getState().showTestData
  const ratingsRef = collection(db, 'ratings')
  const ratingsQuery = query(
    ratingsRef,
    where('rateeId', '==', userId),
    where('testData', '==', showTestData)
  )
  const ratingsSnap = await getDocs(ratingsQuery)

  const totalRatings = ratingsSnap.size
  const tickBreakdown: TickBreakdown = {
    showedUp: 0,
    divedResponsibly: 0,
    knewGearSetup: 0,
    goodBuddy: 0,
    wouldDiveAgain: 0
  }

  ratingsSnap.forEach(docSnap => {
    const rating = docSnap.data() as Omit<Database.RatingDocument, 'id'>
    if (rating.showedUp) tickBreakdown.showedUp++
    if (rating.divedResponsibly) tickBreakdown.divedResponsibly++
    if (rating.knewGearSetup) tickBreakdown.knewGearSetup++
    if (rating.goodBuddy) tickBreakdown.goodBuddy++
    if (rating.wouldDiveAgain) tickBreakdown.wouldDiveAgain++
  })

  return {
    displayName: user.displayName,
    handle: user.handle,
    suburb: user.suburb,
    ratingAggregates,
    tickBreakdown,
    totalRatings,
    accreditationType: user.accreditation?.type ?? null,
    accreditationLevel: user.accreditation?.level ?? null,
    accreditationVerified: user.accreditation?.status === 'verified'
  }
}
